import { useMemo } from 'react'

import { pick } from 'ramda'

import { IQueryConfig } from 'api/report/query/types'
import { FilterState } from 'modules/filter/types'

import useDateRange from './useDateRange'
import useFilters from './useFilters'
import useTimezone from './useTimezone'

export interface UseQueryConfigProps {
  metric: string
}

export interface QueryConfigHook extends UseQueryConfigProps {
  queryConfig: IQueryConfig
  prevQueryConfig: IQueryConfig
}

const FILTERS_TO_PICK = [
  'botStatus',
  'compoundQuery',
  'teamSelected',
  'workspaceAgents',
  'agentsSelected',
  'widgetStatus',
  'inboxSelected',
]

const toQueryConfig = (
  metric: string,
  filters: FilterState,
  timezone: string,
  startDate: string,
  endDate: string
): IQueryConfig => ({
  ...pick(FILTERS_TO_PICK, filters),
  timezone,
  classificationId: filters.selectedClassificationId,
  metric,
  startDate,
  endDate,
})

const useQueryConfig = (metric = ''): QueryConfigHook => {
  const filters = useFilters()
  const timezone = useTimezone()
  const dateRange = useDateRange()

  const [queryConfig, prevQueryConfig] = useMemo(
    () => [
      toQueryConfig(
        metric,
        filters,
        timezone,
        dateRange.startDate,
        dateRange.endDate
      ),
      toQueryConfig(
        metric,
        filters,
        timezone,
        dateRange.prevStartDate,
        dateRange.prevEndDate
      ),
    ],
    [
      metric,
      filters,
      timezone,
      dateRange.startDate,
      dateRange.endDate,
      dateRange.prevStartDate,
      dateRange.prevEndDate,
    ]
  )

  return {
    metric,
    queryConfig,
    prevQueryConfig,
  }
}

export default useQueryConfig
